import React from "react";
import { ChordFingering } from "../utils/chordDiagrams";

interface ChordDiagramProps {
  chordName: string;
  fingering: ChordFingering;
  size?: number;
}

const STRINGS = 6;
const FRETS = 5;

export const ChordDiagram: React.FC<ChordDiagramProps> = ({
  chordName,
  fingering,
  size = 80,
}) => {
  const { frets, baseFret = 1, barres = [] } = fingering;
  const padding = size * 0.15;
  const diagramWidth = size - padding * 2;
  const diagramHeight = size - padding * 2;
  const fretSpacing = diagramHeight / (FRETS + 1);

  const maxFret = Math.max(...frets.filter((f) => f > 0), 0);
  const showBaseFret = baseFret > 1 || maxFret > 4;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="bg-white dark:bg-gray-800 rounded-lg shadow-md"
    >
      {/* Fret lines */}
      {Array.from({ length: FRETS + 1 }, (_, i) => (
        <line
          key={`fret-${i}`}
          x1={padding}
          y1={padding + (i * diagramHeight) / (FRETS + 1)}
          x2={size - padding}
          y2={padding + (i * diagramHeight) / (FRETS + 1)}
          stroke="#333"
          strokeWidth={i === 0 ? 2 : 1}
        />
      ))}
      {/* String lines */}
      {Array.from({ length: STRINGS }, (_, i) => (
        <line
          key={`string-${i}`}
          x1={padding + (i * diagramWidth) / (STRINGS - 1)}
          y1={padding}
          x2={padding + (i * diagramWidth) / (STRINGS - 1)}
          y2={size - padding}
          stroke="#333"
          strokeWidth={1}
        />
      ))}
      {/* Barre */}
      {barres.map((barreFret) => {
        const firstString = frets.findIndex((f) => f === barreFret);
        const lastString = STRINGS - 1 - [...frets].reverse().findIndex((f) => f === barreFret);
        if (firstString < 0) return null;
        const y = padding + (barreFret * diagramHeight) / (FRETS + 1) - fretSpacing / 2;
        const x1 = padding + (firstString * diagramWidth) / (STRINGS - 1);
        const x2 = padding + (lastString * diagramWidth) / (STRINGS - 1);
        return (
          <rect
            key={`barre-${barreFret}`}
            x={x1}
            y={y - 4}
            width={x2 - x1}
            height={8}
            rx={4}
            fill="#333"
          />
        );
      })}
      {/* Dots */}
      {frets.map((fret, stringIdx) => {
        if (fret <= 0 || barres.includes(fret)) return null;
        const x = padding + (stringIdx * diagramWidth) / (STRINGS - 1);
        const y = padding + (fret * diagramHeight) / (FRETS + 1) - fretSpacing / 2;
        return (
          <circle
            key={`dot-${stringIdx}`}
            cx={x}
            cy={y}
            r={size * 0.08}
            fill="#333"
          />
        );
      })}
      {/* X for muted strings */}
      {frets.map((fret, stringIdx) => {
        if (fret !== -1) return null;
        const x = padding + (stringIdx * diagramWidth) / (STRINGS - 1);
        const r = size * 0.04;
        return (
          <g key={`mute-${stringIdx}`}>
            <line
              x1={x - r}
              y1={padding - r}
              x2={x + r}
              y2={padding + r}
              stroke="#999"
              strokeWidth={1}
            />
            <line
              x1={x - r}
              y1={padding + r}
              x2={x + r}
              y2={padding - r}
              stroke="#999"
              strokeWidth={1}
            />
          </g>
        );
      })}
      {/* O for open strings */}
      {frets.map((fret, stringIdx) => {
        if (fret !== 0) return null;
        const x = padding + (stringIdx * diagramWidth) / (STRINGS - 1);
        const r = size * 0.04;
        return (
          <circle
            key={`open-${stringIdx}`}
            cx={x}
            cy={padding - r}
            r={r}
            fill="none"
            stroke="#333"
            strokeWidth={1}
          />
        );
      })}
      {/* Chord name */}
      <text
        x={size / 2}
        y={size - padding / 2}
        textAnchor="middle"
        fontSize={size * 0.12}
        fontWeight="bold"
        fill="#333"
      >
        {chordName}
      </text>
      {/* Base fret label */}
      {showBaseFret && baseFret > 1 && (
        <text
          x={padding / 2}
          y={padding + fretSpacing / 2}
          textAnchor="middle"
          fontSize={size * 0.1}
          fill="#666"
        >
          {baseFret}fr
        </text>
      )}
    </svg>
  );
};
